import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput } from '../BubbleUI';
import { updateConfigurations } from '@go-client/lib/api/property';
import { PropertySettings } from '@go-shared/types/property';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { createPortal } from 'react-dom';

interface DDPFormDataBase {
  config: PropertySettings;
}

type DDPFormData = DDPFormDataBase & {
  _dirty: boolean;
  _errors: Partial<DDPFormError>;
};

type DDPFormError = { [K in keyof DDPFormDataBase]: string };

function DEFAULT_FORM_DATA(propConfigs?: PropertySettings): DDPFormData {
  return {
    config: { ...propConfigs },
    _dirty: false,
    _errors: {},
  };
}

interface DDPFormProps {
  edit?: PropertySettings;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function PropertyForm(props: DDPFormProps) {
  const { activeProperty } = useActiveProperty();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<DDPFormData>(DEFAULT_FORM_DATA(props.edit));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof PropertySettings>(key: T, val: PropertySettings[T]) {
    setFormData({ ...formData, _dirty: true, config: { ...formData.config, [key]: val } });
  }

  async function submitForm() {
    setSaving(true);
    try {
      await updateConfigurations(activeProperty._id, formData.config);
      await props.onFormSubmitted();
    } finally {
      setSaving(false);
    }
  }

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      <BubbleFormGroup label="Limit Past Days" className="col-span-3">
        <div className="flex items-start">
          <BubbleInput
            type="checkbox"
            checked={formData.config.ddpDaysBeforeLimit !== undefined}
            onValue={check => {
              changeForm('ddpDaysBeforeLimit', check ? 0 : undefined);
            }}
            className="px-0-i pt-2 mr-2 leading-none"
          />
          <BubbleInput
            type="number"
            disabled={formData.config.ddpDaysBeforeLimit === undefined}
            value={formData.config.ddpDaysBeforeLimit}
            onValue={val => changeForm('ddpDaysBeforeLimit', val)}
            min={0}
            block
            className="flex-grow"
          />
        </div>
        <div className="text-slate italic mt-1">
          Limit amount of days the DDP is allowed to go into the past
        </div>
      </BubbleFormGroup>
      <BubbleFormGroup label="Limit Future Days" className="col-span-3">
        <div className="flex items-start">
          <BubbleInput
            type="checkbox"
            checked={formData.config.ddpDaysAfterLimit !== undefined}
            onValue={check => {
              changeForm('ddpDaysAfterLimit', check ? 0 : undefined);
            }}
            className="px-0-i pt-2 mr-2 leading-none"
          />
          <BubbleInput
            type="number"
            disabled={formData.config.ddpDaysAfterLimit === undefined}
            value={formData.config.ddpDaysAfterLimit}
            onValue={val => changeForm('ddpDaysAfterLimit', val)}
            min={0}
            block
            className="flex-grow"
          />
        </div>
        <div className="text-slate italic mt-1">
          Limit amount of days the DDP is allowed to go into the future
        </div>
      </BubbleFormGroup>
      <BubbleFormGroup label="Day Before Overflow (Hours)" className="col-span-3">
        <div className="flex items-start">
          <BubbleInput
            type="checkbox"
            checked={formData.config.prevDayOverflowHrs !== undefined}
            onValue={check => {
              changeForm('prevDayOverflowHrs', check ? 0 : undefined);
            }}
            className="px-0-i pt-2 mr-2 leading-none"
          />
          <BubbleInput
            type="number"
            disabled={formData.config.prevDayOverflowHrs === undefined}
            value={formData.config.prevDayOverflowHrs}
            onValue={val => changeForm('prevDayOverflowHrs', val)}
            min={1}
            max={24}
            block
            className="flex-grow"
          />
        </div>
        <div className="text-slate italic mt-1">
          Show a portion of the previous day's lineup on the current day. Valid values: 1 - 24.
        </div>
      </BubbleFormGroup>
      <BubbleFormGroup label="Day After Overflow (Hours)" className="col-span-3">
        <div className="flex items-start">
          <BubbleInput
            type="checkbox"
            checked={formData.config.nextDayOverflowHrs !== undefined}
            onValue={check => {
              changeForm('nextDayOverflowHrs', check ? 0 : undefined);
            }}
            className="px-0-i pt-2 mr-2 leading-none"
          />
          <BubbleInput
            type="number"
            disabled={formData.config.nextDayOverflowHrs === undefined}
            value={formData.config.nextDayOverflowHrs}
            onValue={val => changeForm('nextDayOverflowHrs', val)}
            min={1}
            max={24}
            block
            className="flex-grow"
          />
        </div>
        <div className="text-slate italic mt-1">
          Show a portion of the next day's lineup on the current day. Only applicable during the
          time window between midnight and day start. Valid values: 1 - 24.
        </div>
      </BubbleFormGroup>
      <div className="col-span-3">
        <div className="flex items-start relative">
          <BubbleInput
            type="checkbox"
            checked={!!formData.config.useLegacyDdpContentSrc}
            onValue={check => {
              changeForm('useLegacyDdpContentSrc', check);
            }}
            inputClassName="stretched-link"
            className="mr-1"
          />
          <span>Use Legacy Content Source</span>
        </div>
        <div className="text-slate italic mt-1">
          Activities tab of the DDP will show the same content as the print program, as long as it
          is not marked hidden from Digital Daily Program.
        </div>
      </div>
    </div>
  );

  const formActions = (
    <div className="w-full flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
